import { useSystemStore } from "@/stores/systemStore";
import {
  GetCurrentLocation,
  HynixCoordinate,
  LineTypeCode,
  Location,
  PROVIDE_KEY_CURRENT_LOCATION,
  PROVIDE_KEY_GET_CURRENT_LOCATION,
} from "@/types";
import calculateDistance from "@/use/useCalculateDistance";
import { inject, Ref, watch } from "vue";

export type ShouldGetBusLocationResult = {
  shouldGetLocation: boolean;
  locationNotAvailable: boolean;
  nearbyCampusName?: string;
};

const systemStore = useSystemStore();

function waitForCurrentLocation(
  currentLocation: Ref<Location | undefined>,
  timeout = 5000
): Promise<Location | undefined> {
  return new Promise((resolve) => {
    const stop = watch(currentLocation, (location) => {
      stop();
      resolve(location);
    });

    // 타임아웃 처리 (5초 후에도 위치 미수신)
    setTimeout(() => {
      stop();
      resolve(undefined);
    }, timeout);
  });
}

function getNearbyCampusName(
  location?: Location,
  campusName?: string
): string | undefined {
  // 위치 정보가 없으면 즉시 반환
  if (!location) return undefined;

  const hynixCoordinateList = systemStore.campusCoordinateList;

  // 위치와 캠퍼스 간 거리가 반경 내에 있는지 확인하는 헬퍼 함수
  const isCampusInRange = (
    campusCoord: HynixCoordinate,
    userLocation: Location
  ): boolean => {
    const distanceInMeters =
      calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        campusCoord.lat,
        campusCoord.lng
      ) * 1000;

    return distanceInMeters < campusCoord.radiusMeter;
  };

  // 주어진 캠퍼스명이 있으면 해당 캠퍼스를 먼저 확인
  if (campusName) {
    const specifiedCampus = hynixCoordinateList.find(
      (coord) => coord.campusName === campusName
    );

    if (specifiedCampus && isCampusInRange(specifiedCampus, location)) {
      return campusName;
    }
  }

  // 사용자 위치 기준으로 가장 가까운 캠퍼스 찾기
  const nearbyCampus = hynixCoordinateList.find((campus) =>
    isCampusInRange(campus, location)
  );

  return nearbyCampus?.campusName;
}

/**
 * 버스 위치를 조회해야 하는지 여부를 판단한다.
 *
 * - 사내 셔틀(LineTypeCode.Inside)인 경우 현재 위치 기반으로 캠퍼스 반경 X미터 내에 있어야 실시간 버스 위치 조회가 허용된다.
 * - 외부 노선일 경우 별도의 위치 확인 없이 바로 조회하도록 허용한다.
 * - 네이티브 앱에 위치 정보를 요청하고, 일정 시간 내 응답이 없으면 위치 정보 비활성화로 간주한다.
 *
 * @param lineTypeCode - 노선 유형 코드.
 * @param campusName - 캠퍼스 이름 (optional). 제공되지 않으면 모든 캠퍼스를 순회하여 반경 내에 있는지를 판단한다.
 * @returns 현재 위치 기반으로 버스 위치 조회 가능 여부를 포함한 객체를 반환한다.
 */
export function useShouldGetBusLocation() {
  const getCurrentLocation = inject(
    PROVIDE_KEY_GET_CURRENT_LOCATION
  ) as GetCurrentLocation;

  const currentLocation = inject(PROVIDE_KEY_CURRENT_LOCATION) as Ref<
    Location | undefined
  >;

  const shouldGetBusLocationAndFindCampus = async (
    lineTypeCode: LineTypeCode,
    campusName?: string
  ): Promise<ShouldGetBusLocationResult> => {
    if (lineTypeCode !== LineTypeCode.Inside) {
      return {
        shouldGetLocation: true,
        locationNotAvailable: false,
        nearbyCampusName: campusName,
      };
    }

    // 네이티브 앱에 현재 위치 요청
    getCurrentLocation();
    const location = await waitForCurrentLocation(currentLocation);

    if (!location || location.latitude === -1 || location.longitude === -1) {
      return {
        shouldGetLocation: false,
        locationNotAvailable: true,
        nearbyCampusName: campusName,
      };
    }

    const nearbyCampusName = getNearbyCampusName(location);

    return {
      shouldGetLocation: !!nearbyCampusName,
      locationNotAvailable: false,
      nearbyCampusName,
    };
  };

  return { shouldGetBusLocationAndFindCampus };
}
